import React, { useEffect, useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { processFlow, submitConversation } from '@/store/slices/middleware/chatbotFlowThunk';
import LoadBotMessage from '@/components/chatbot/Layout/messages/LoadBotMessage';
import store from '@/store/store';
import BlockTypes from '@/constants/BlockTypes';
import MessageBlock from '@/components/chatbot/Layout/content/MessageBlock';
import DataBlock from '@/components/chatbot/Layout/content/DataBlock';
import { getActiveBlock, hasAnswer } from '@/helpers/Flow';
import { animateScroll } from 'react-scroll';
import styles from './styles/block-manager.module.css';

/**
 * Custom hook for managing side effects related to the chatbot.
 * Handles scrolling to the bottom, re-checking scroll position,
 * and observing changes in the chat container.
 *
 * @param {Array} conversation - The current conversation state.
 * @param {boolean} isEverOpened - Whether the chatbot has ever been opened.
 * @param {boolean} isFinished - Whether the conversation is finished.
 * @param {boolean} loading - Whether the chatbot is currently loading.
 * @returns {Object} - Contains containerRef for the chat container.
 */
const useChatbotEffects = (conversation, isEverOpened, isFinished, loading) => {
  // Ref to the chat container element
  const containerRef = useRef(null);
  // State to track if auto-scrolling is enabled
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);  // Track if submission has already occurred
  const scrollThreshold = 100;

  // Scrolls to the bottom of the chat container.
  const scrollToBottom = useCallback((duration) => {
    let shouldScroll = autoScrollEnabled;
    if (hasAnswer(getActiveBlock(conversation)))
      shouldScroll = true;

    if (containerRef.current && shouldScroll) {
      animateScroll.scrollToBottom({
        duration,
        containerId: 'elay-chatbot-messages-wrapper',
      });
    }
  });

  // Set up a MutationObserver to handle dynamic content size changes (when something inside a message is being rendered)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      scrollToBottom(250);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, subtree: true });
    }

    return () => {
      if (containerRef.current) {
        observer.disconnect();
      }
    };
  });

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      const scrollTop = Math.ceil(container.scrollTop);
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // make sure the scrollable height is at least 0
      const scrollableHeight = Math.max(0, scrollHeight - clientHeight - scrollThreshold);

      // Determine if user is scrolling up or down
      if (scrollTop < scrollableHeight) {
        setAutoScrollEnabled(false);
      } else {
        setAutoScrollEnabled(true);
      }
    }
  });

  // Effect to handle scroll events
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  });

  // Effect to handle browser unload events
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isFinished && conversation.length > 1 && !hasSubmitted) {
        setHasSubmitted(true);  // Prevent multiple submissions
        store.dispatch(submitConversation());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleBeforeUnload);
    window.addEventListener('close', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleBeforeUnload);
      window.removeEventListener('close', handleBeforeUnload);
    };
  });

  // Effect to process the flow if the chatbot has ever been opened
  useEffect(() => {
    if (isEverOpened) {
      store.dispatch(processFlow()).then(() => {
        handleScroll();
        scrollToBottom(250);
      });
    }
  }, [isEverOpened, conversation]);

  return { containerRef };
};

const BlockManager = (props) => {
  const { conversation, loading, dataLoading, isFinished, isEverOpened } = props;
  const { containerRef } = useChatbotEffects(conversation, isEverOpened, isFinished, loading);
  const activeBlock = getActiveBlock(conversation);

  return (
    <div id="elay-chatbot-messages-wrapper" className={styles.messages_wrapper} ref={containerRef}>
      {conversation.map((block, i) => {
        // Determine if the block is the active one
        const isActive = activeBlock.id === block.id &&
          i === conversation.length - 1 &&
          !loading &&
          !isFinished;

        // Render DataBlock or MessageBlock based on block type
        if (block?.options?.default_answer === true) return null;

        return block.blockType === BlockTypes.DATA ? (
          <DataBlock key={i} block={block} isActive={isActive} />
        ) : (
          <MessageBlock key={i} block={block} isActive={isActive} />
        );
      })}
      {(loading || dataLoading) && <LoadBotMessage />}
    </div>
  );
};

// Map Redux state to component props
const mapStateToProps = (state) => ({
  flow: state.chatbotFlow.flow,
  loading: state.chatbotFlow.loading,
  dataLoading: state.chatbotFlow.dataLoading,
  isFinished: state.chatbotFlow.isFinished,
  conversation: state.chatbotFlow.conversation,
  isEverOpened: state.chatbotVisibility.isEverOpened,
  UUID: state.chatbotDetails.UUID,
});

export default connect(mapStateToProps)(BlockManager);
